import React, { useMemo, useState, useCallback, useId } from 'react';
import { FaLayerGroup, FaChevronDown, FaPlay, FaPowerOff } from 'react-icons/fa';
import VmCard from './VmCard';
import VmTable from './VmTable';
import { VIEW_MODE } from '../utils/constants';
import { countByPowerState } from '../utils/format';

/** Chiave stabile di una sezione prodotto (ambiente + prodotto). */
const sectionKey = (environment, product) => `${environment}::${product}`;

/** Intestazione di un gruppo ambiente con conteggio. */
function EnvironmentHeader({ environment, count }) {
  return (
    <div className="flex items-center gap-2">
      <FaLayerGroup className="text-primary" size={15} aria-hidden="true" />
      <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">
        {environment}
      </h2>
      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary dark:bg-primary/20">
        {count} VM
      </span>
    </div>
  );
}

/** Riepilogo compatto acceso/spento mostrato nell'intestazione del prodotto. */
function PowerSummary({ vms }) {
  const c = countByPowerState(vms);
  return (
    <span className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
      {c.ON > 0 && (
        <span className="inline-flex items-center gap-1" title={`${c.ON} accese`}>
          <FaPlay className="text-emerald-500" size={9} aria-hidden="true" />
          {c.ON}
        </span>
      )}
      {c.OFF > 0 && (
        <span className="inline-flex items-center gap-1" title={`${c.OFF} spente`}>
          <FaPowerOff className="text-gray-400" size={9} aria-hidden="true" />
          {c.OFF}
        </span>
      )}
      {c.SUSPENDED + c.UNKNOWN > 0 && (
        <span
          className="inline-flex items-center gap-1"
          title={`${c.SUSPENDED + c.UNKNOWN} sospese o sconosciute`}
        >
          <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
          {c.SUSPENDED + c.UNKNOWN}
        </span>
      )}
    </span>
  );
}

/**
 * Sezione prodotto collassabile. Collassata mostra solo nome, conteggio e
 * riepilogo di stato; cliccando l'intestazione si rendono visibili le macchine
 * (griglia di card o tabella). Accessibile: header come button con
 * aria-expanded/aria-controls.
 */
function ProductSection({
  environment,
  product,
  vms,
  open,
  onToggle,
  viewMode,
  rowProps,
}) {
  const bodyId = useId();
  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <h3 className="m-0">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors motion-reduce:transition-none hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-gray-900/40"
        >
          <FaChevronDown
            size={13}
            aria-hidden="true"
            className={`shrink-0 text-gray-400 transition-transform motion-reduce:transition-none ${
              open ? 'rotate-180' : ''
            }`}
          />
          <span className="flex-1 truncate text-sm font-semibold text-gray-800 dark:text-gray-100">
            {product}
          </span>
          <PowerSummary vms={vms} />
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary dark:bg-primary/20">
            {vms.length} VM
          </span>
        </button>
      </h3>

      {open && (
        <div
          id={bodyId}
          className="border-t border-gray-100 p-4 dark:border-gray-700"
        >
          {viewMode === VIEW_MODE.TABLE ? (
            <VmTable vms={vms} {...rowProps} />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {vms.map((vm) => (
                <VmCard
                  key={vm.catalogId}
                  vm={vm}
                  catalogWarnings={rowProps.catalogWarnings}
                  op={rowProps.operations[vm.catalogId]}
                  busy={rowProps.isBusy(vm.catalogId)}
                  onRequestAction={rowProps.onRequestAction}
                  onDismissOp={rowProps.onDismissOp}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Rende l'inventario VM raggruppato prima per ambiente e poi per prodotto, con
 * ogni prodotto in una sezione collassabile (collassata di default). Le
 * macchine diventano visibili cliccando sull'intestazione del prodotto.
 */
export default function VmGroups({
  groups,
  viewMode,
  catalogWarnings,
  operations,
  isBusy,
  onRequestAction,
  onDismissOp,
}) {
  const rowProps = {
    catalogWarnings,
    operations,
    isBusy,
    onRequestAction,
    onDismissOp,
  };

  // Elenco di tutte le chiavi di sezione prodotto correnti.
  const allKeys = useMemo(
    () =>
      groups.flatMap((g) =>
        g.products.map((p) => sectionKey(g.environment, p.product)),
      ),
    [groups],
  );

  // Sezioni aperte (default: tutte collassate).
  const [openKeys, setOpenKeys] = useState(() => new Set());

  const toggle = useCallback((key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setOpenKeys(new Set(allKeys)), [allKeys]);
  const collapseAll = useCallback(() => setOpenKeys(new Set()), []);

  const allOpen = allKeys.length > 0 && openKeys.size >= allKeys.length;

  return (
    <div className="space-y-8">
      {/* Controlli espandi/comprimi tutto */}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={expandAll}
          disabled={allOpen}
          className="rounded-lg px-2.5 py-1 text-xs font-medium text-primary transition-colors motion-reduce:transition-none hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          Espandi tutti
        </button>
        <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
        <button
          type="button"
          onClick={collapseAll}
          disabled={openKeys.size === 0}
          className="rounded-lg px-2.5 py-1 text-xs font-medium text-gray-500 transition-colors motion-reduce:transition-none hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          Comprimi tutti
        </button>
      </div>

      {groups.map((group) => (
        <section key={group.environment} className="space-y-4">
          <EnvironmentHeader environment={group.environment} count={group.count} />

          <div className="space-y-3">
            {group.products.map((sub) => {
              const key = sectionKey(group.environment, sub.product);
              return (
                <ProductSection
                  key={key}
                  environment={group.environment}
                  product={sub.product}
                  vms={sub.vms}
                  open={openKeys.has(key)}
                  onToggle={() => toggle(key)}
                  viewMode={viewMode}
                  rowProps={rowProps}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
